import { useState } from 'react'
import { Dropdown } from '@/customComponents/uiComponents/dropdown/Dropdown.jsx'
import { Link } from 'react-router'
import { notificationBell } from '@img/index.js'
import NotifyDot from '@/customComponents/uiComponents/global/NotifyDot.jsx'
import { useNotifications, useUnreadCount, useMarkAsRead } from '@/hooks/useNotifications'
import {formatTimeAgo} from '@/utils/functions'

export default function NotificationDropdown() {
  const [isOpen, setIsOpen] = useState(false)
  const { data } = useNotifications({ page: 1, perPage: 5, unreadOnly: true })
  const { data: unreadCount } = useUnreadCount()
  const markAsRead = useMarkAsRead()

  const items = data?.data || []
  const hasUnread = (unreadCount || 0) > 0

  const handleNotificationClick = (n) => {
    if (!n.is_read) markAsRead.mutate(n.id)
    setIsOpen(false)
  }

  return (
    <div className="relative">
      <button
        className="cursor-pointer dropdown-toggle relative flex items-center justify-center text-gray-500 transition-colors hover:text-gray-700"
        onClick={() => setIsOpen(!isOpen)}
      >
        <NotifyDot show={hasUnread} />
        <img alt="notification" src={notificationBell}/>
      </button>

      <Dropdown
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        className="max-md:fixed max-md:top-16 max-md:left-0 max-md:right-0 max-md:w-auto max-md:mt-0 absolute left-0 sm:right-0 sm:left-auto mt-4.25 flex h-96 sm:h-120 w-87.5 flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-lg"
      >
        <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100">
          <h5 className="text-lg font-semibold text-gray-800">
            Notification
            {hasUnread && <span className="ml-2 text-sm font-medium text-gray-500">({unreadCount})</span>}
          </h5>
          <button onClick={() => setIsOpen(false)} className="text-gray-500 transition hover:text-gray-700">
            <svg className="fill-current" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M6.21967 7.28131C5.92678 6.98841 5.92678 6.51354 6.21967 6.22065C6.51256 5.92775 6.98744 5.92775 7.28033 6.22065L11.999 10.9393L16.7176 6.22078C17.0105 5.92789 17.4854 5.92788 17.7782 6.22078C18.0711 6.51367 18.0711 6.98855 17.7782 7.28144L13.0597 12L17.7782 16.7186C18.0711 17.0115 18.0711 17.4863 17.7782 17.7792C17.4854 18.0721 17.0105 18.0721 16.7176 17.7792L11.999 13.0607L7.28033 17.7794C6.98744 18.0722 6.51256 18.0722 6.21967 17.7794C5.92678 17.4865 5.92678 17.0116 6.21967 16.7187L10.9384 12L6.21967 7.28131Z" fill="currentColor" />
            </svg>
          </button>
        </div>

        <ul className="flex flex-col h-auto overflow-y-auto">
          {items.length === 0 ? (
            <li className="p-4 text-center text-gray-400 text-sm">No unread notifications</li>
          ) : (
            items.map((n) => {
              const inner = (
                <>
                  <span className="relative block w-10 h-10 rounded-full shrink-0 bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-600">
                    {(n.rendered_push_title || n.notification_name || '?').charAt(0)}
                  </span>
                  <span className="block flex-1 min-w-0">
                    <span className="mb-1.5 block text-sm text-gray-500">
                      <span className="font-medium text-gray-800">{n.rendered_push_title || n.notification_name}</span>
                    </span>
                    {n.rendered_push_body && (
                      <span className="block text-xs text-gray-500 truncate">{n.rendered_push_body}</span>
                    )}
                    <span className="block text-xs text-gray-400 mt-1">{formatTimeAgo(n.delivered_at)}</span>
                  </span>
                  {!n.is_read && <span className="w-2 h-2 bg-green-500 rounded-full shrink-0 mt-2"></span>}
                </>
              )
              const className = "flex gap-3 rounded-lg border-b border-gray-100 p-3 hover:bg-gray-100 cursor-pointer"
              return (
                <li key={n.id}>
                  {n.trip_id ? (
                    <Link
                      to={`/rides/${n.trip_id}`}
                      onClick={() => handleNotificationClick(n)}
                      className={className}
                    >
                      {inner}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleNotificationClick(n)}
                      className={`block w-full text-left ${className}`}
                    >
                      {inner}
                    </button>
                  )}
                </li>
              )
            })
          )}
        </ul>

        <Link
          to="/notifications"
          onClick={() => setIsOpen(false)}
          className="block px-4 py-2 mt-3 text-sm font-medium text-center text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
        >
          View All Notifications
        </Link>
      </Dropdown>
    </div>
  )
}
